import { API_BASE_URL } from '../api';
import { authService } from './authService';
import { GiftVoucher, CreateGiftVoucherRequest, GiftVoucherStats } from '../types/giftVoucher';

export const giftVoucherService = {
  // Créer un bon cadeau
  async create(data: CreateGiftVoucherRequest): Promise<GiftVoucher> {
    // Slash final volontaire : la route backend est POST /api/gift-vouchers/.
    // Sans lui, FastAPI renvoie un 307 vers l'URL avec slash, et derrière un
    // proxy qui termine le TLS en amont (Cloudflare en preprod) le Location
    // repasse en http:// → blocage « Mixed Content » du navigateur.
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      let detail = 'Erreur lors de la création du bon cadeau';
      try {
        const error = await response.json();
        if (error && (error.detail || error.message)) {
          detail = error.detail || error.message;
        }
      } catch (_) {
        // Fallback: read text (e.g., Internal Server Error HTML/plain text)
        const text = await response.text();
        if (text) detail = text;
      }
      throw new Error(detail);
    }
    
    return response.json();
  },

  // Marquer un bon cadeau comme payé
  async markPaid(voucherId: number, stripePaymentIntentId: string): Promise<{ success: boolean; voucher_id: number; payment_status: string; stripe_payment_intent_id: string; }> {
    const url = new URL(`${API_BASE_URL}/api/gift-vouchers/${voucherId}/mark-paid`);
    url.searchParams.set('stripe_payment_intent_id', stripePaymentIntentId);

    const response = await fetch(url.toString(), {
      method: 'PUT',
    });

    if (!response.ok) {
      let detail = 'Erreur lors de la confirmation du paiement du bon cadeau';
      try {
        const error = await response.json();
        if (error && (error.detail || error.message)) {
          detail = error.detail || error.message;
        }
      } catch (_) {
        const text = await response.text();
        if (text) detail = text;
      }
      throw new Error(detail);
    }

    return response.json();
  },

  // Lister tous les bons cadeaux (backoffice)
  async list(params?: { status?: string; limit?: number; offset?: number }): Promise<GiftVoucher[]> {
    const url = new URL(`${API_BASE_URL}/api/gift-vouchers/list`);
    if (params?.status) url.searchParams.set('status', params.status);
    if (params?.limit) url.searchParams.set('limit', params.limit.toString());
    if (params?.offset) url.searchParams.set('offset', params.offset.toString());

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des bons cadeaux');
    }
    return response.json();
  },

  // Récupérer un bon cadeau par ID
  async getById(voucherId: number): Promise<GiftVoucher> {
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/by-id/${voucherId}`);
    if (!response.ok) {
      let detail = 'Bon cadeau non trouvé';
      try {
        const error = await response.json();
        if (error && (error.detail || error.message)) {
          detail = error.detail || error.message;
        }
      } catch (_) {
        const text = await response.text();
        if (text) detail = text;
      }
      throw new Error(detail);
    }
    return response.json();
  },

  // Récupérer un bon cadeau par numéro
  async getByNumber(voucherNumber: string): Promise<GiftVoucher> {
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/${voucherNumber}`);
    
    if (!response.ok) {
      let detail = 'Bon cadeau non trouvé';
      try {
        const error = await response.json();
        if (error && (error.detail || error.message)) {
          detail = error.detail || error.message;
        }
      } catch (_) {
        const text = await response.text();
        if (text) detail = text;
      }
      throw new Error(detail);
    }
    
    return response.json();
  },

  // Récupérer un bon cadeau avec les informations du service
  async getWithService(voucherNumber: string): Promise<{
    found: boolean;
    error?: string;
    voucher?: GiftVoucher;
    service?: {
      id: number;
      name: string;
      price?: number;
      duration_minutes: number;
      category_name?: string;
    };
    is_valid: boolean;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/${voucherNumber}/with-service`);
    
    if (!response.ok) {
      let detail = 'Erreur lors de la récupération du bon cadeau';
      try {
        const error = await response.json();
        if (error && (error.detail || error.message)) {
          detail = error.detail || error.message;
        }
      } catch (_) {
        const text = await response.text();
        if (text) detail = text;
      }
      throw new Error(detail);
    }
    
    return response.json();
  },

  // Rechercher des bons cadeaux par email
  async searchByEmail(email: string): Promise<GiftVoucher[]> {
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/search?email=${encodeURIComponent(email)}`);
    
    if (!response.ok) {
      let detail = 'Erreur lors de la recherche';
      try {
        const error = await response.json();
        if (error && (error.detail || error.message)) {
          detail = error.detail || error.message;
        }
      } catch (_) {
        const text = await response.text();
        if (text) detail = text;
      }
      throw new Error(detail);
    }
    
    return response.json();
  },

  // Valider un bon cadeau
  async validate(voucherNumber: string): Promise<GiftVoucher> {
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/${voucherNumber}/validate`, {
      method: 'POST',
    });
    
    if (!response.ok) {
      let detail = 'Erreur lors de la validation';
      try {
        const error = await response.json();
        if (error && (error.detail || error.message)) {
          detail = error.detail || error.message;
        }
      } catch (_) {
        const text = await response.text();
        if (text) detail = text;
      }
      throw new Error(detail);
    }
    
    return response.json();
  },

  // Utiliser un bon cadeau
  async use(voucherNumber: string, bookingId: number): Promise<GiftVoucher> {
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/${voucherNumber}/use`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ booking_id: bookingId }),
    });
    
    if (!response.ok) {
      let detail = "Erreur lors de l'utilisation du bon";
      try {
        const error = await response.json();
        if (error && (error.detail || error.message)) {
          detail = error.detail || error.message;
        }
      } catch (_) {
        const text = await response.text();
        if (text) detail = text;
      }
      throw new Error(detail);
    }
    
    return response.json();
  },

  // Obtenir les statistiques
  async getStats(): Promise<GiftVoucherStats> {
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/stats`);
    
    if (!response.ok) {
      let detail = 'Erreur lors de la récupération des statistiques';
      try {
        const error = await response.json();
        if (error && (error.detail || error.message)) {
          detail = error.detail || error.message;
        }
      } catch (_) {
        const text = await response.text();
        if (text) detail = text;
      }
      throw new Error(detail);
    }
    
    return response.json();
  },

  // Invalider un bon cadeau (backoffice)
  async invalidate(voucherId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/${voucherId}/invalidate`, {
      method: 'POST',
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erreur lors de l\'invalidation' }));
      throw new Error(error.detail || 'Erreur lors de l\'invalidation');
    }
    
    return response.json();
  },

  // Prolonger la validité d'un bon (backoffice)
  async extend(voucherId: number, months: number): Promise<{ success: boolean; new_expiry_date: string; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/${voucherId}/extend?months=${months}`, {
      method: 'POST',
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erreur lors de la prolongation' }));
      throw new Error(error.detail || 'Erreur lors de la prolongation');
    }
    
    return response.json();
  },

  // Changer le service associé à un bon (backoffice)
  async changeService(voucherId: number, serviceId: number): Promise<{
    success: boolean;
    old_service_name: string;
    new_service: { id: number; name: string };
    message: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/${voucherId}/change-service`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ service_id: serviceId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erreur lors du changement de service' }));
      throw new Error(error.detail || 'Erreur lors du changement de service');
    }

    return response.json();
  },

  // Renvoyer à l'acheteur l'email de confirmation avec le bon en PDF (backoffice)
  async resendEmail(voucherId: number): Promise<{ success: boolean; sent_to: string; message: string }> {
    const headers = await authService.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/${voucherId}/resend-email`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Erreur lors du renvoi de l'email" }));
      throw new Error(error.detail || "Erreur lors du renvoi de l'email");
    }

    return response.json();
  },

  // Télécharger le PDF d'un bon (backoffice)
  async downloadPdf(voucherId: number): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/api/gift-vouchers/${voucherId}/download-pdf`);
    
    if (!response.ok) {
      throw new Error('Erreur lors du téléchargement du PDF');
    }
    
    return response.blob();
  },
};
